import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  getScopedSensorAlarms,
  getScopedSensorSessions,
  getScopedSensorConnectivity,
  liveRows,
} from "../scopedTelemetry";
import { useSensorTenure } from "./useSensorTenure";
import { scopeKeyOf, type TenureScope } from "../../../services/installationTenure";
import {
  DEFAULT_SECTIONS,
  issuesForUnit,
  summariseUnit,
  weeklyWindow,
  type Issue,
  type ReportWindow,
  type UnitReport,
} from "../buildingReport";

/**
 * One sensor's weekly report.
 *
 * Deliberately the same `summariseUnit` the property report and the emailed
 * report use, over the same window, so a unit's numbers here cannot disagree
 * with the row it occupies on its property's report. That is the whole reason
 * this is not its own arithmetic.
 *
 * Query keys match `useBuildingReport`'s, so arriving from a property report is
 * a cache hit rather than a second fetch of the same three streams. They carry
 * the sensor's location-scope key as well, for the reason `scopeKeyOf` gives.
 */

const STALE_TIME = 10 * 60_000;
const GC_TIME = 2 * 60 * 60_000;
const REFETCH_INTERVAL = 60_000 * 60;

const ALERT_PAGE_SIZE = 5000;
const SESSION_PAGE_SIZE = 2000;
const CONNECTIVITY_PAGE_SIZE = 500;

export type SensorReport = {
  scsName: string;
  name: string;
  periodLabel: string;
  sections: string[];
  unit: UnitReport;
  /** Why this sensor would be listed on its property's report, if at all. */
  issues: Issue[];
};

export type SensorReportResult = {
  report: SensorReport | null;
  status: "loading" | "ready" | "error";
  window: ReportWindow;
  /** How the figures are scoped, so the page can say so. A scope with no cutoff
   *  means these numbers may span more than one location. */
  tenure: TenureScope;
};

function isoBound(ms: number): string {
  return new Date(ms).toISOString().slice(0, 19);
}

export function useSensorReport(
  scsName: string,
  displayName: string,
  now?: Date
): SensorReportResult {
  // Recomputed only when the day changes, so a render cannot move the
  // reporting period under the reader.
  const window = useMemo(
    () => weeklyWindow(now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [now ? now.getTime() : new Date().toDateString()]
  );

  // Resolves before any of the three reads below, which are gated on it: the
  // rows they return depend on the cutoff, so fetching first and filtering later
  // would write unscoped rows into keys that claim to be scoped.
  const { scope, settled } = useSensorTenure(scsName);
  const scopeKey = scopeKeyOf(scope);

  const [alarms, sessions, connectivity] = useQueries({
    queries: [
      {
        queryKey: ["alerts-agg", scsName, scopeKey],
        queryFn: async () =>
          await getScopedSensorAlarms(scsName, ALERT_PAGE_SIZE, scope),
        enabled: !!scsName && settled,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false,
        refetchInterval: REFETCH_INTERVAL,
      },
      {
        // Both periods in one fetch, bounded at the start of the comparison
        // window, so the comparison costs one read rather than two.
        queryKey: [
          "report-sessions",
          scsName,
          window.priorStart,
          window.end,
          scopeKey,
        ],
        queryFn: async () =>
          await getScopedSensorSessions(
            scsName,
            SESSION_PAGE_SIZE,
            isoBound(window.priorStart),
            isoBound(window.end),
            scope
          ),
        enabled: !!scsName && settled,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false,
        refetchInterval: REFETCH_INTERVAL,
      },
      {
        queryKey: ["report-connectivity", scsName, scopeKey],
        queryFn: async () =>
          await getScopedSensorConnectivity(
            scsName,
            CONNECTIVITY_PAGE_SIZE,
            scope
          ),
        enabled: !!scsName && settled,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false,
        refetchInterval: REFETCH_INTERVAL,
      },
    ],
  });

  const signature = [alarms, sessions, connectivity]
    .map((q) => `${q.status}:${q.dataUpdatedAt}`)
    .join("|");

  return useMemo<SensorReportResult>(() => {
    // Only alarms and sessions are fatal: without them there is no report.
    // A failed CONNECTIVITY read is not, and must not be, because the whole
    // point of the branch below is that it degrades to "we could not see"
    // rather than to a claim. Gating on it here made that branch unreachable
    // and turned a connectivity blip into a blank page.
    if ([alarms, sessions].some((q) => q.status === "error")) {
      return { report: null, status: "error", window, tenure: scope };
    }
    if (!settled || [alarms, sessions].some((q) => q.status === "pending")) {
      return { report: null, status: "loading", window, tenure: scope };
    }
    // Connectivity still has to settle, so the equipment section is not read
    // as unknown purely because it had not arrived yet.
    if (connectivity.status === "pending") {
      return { report: null, status: "loading", window, tenure: scope };
    }

    const alarmRows = alarms.data?.data ?? [];
    const sessionRows = sessions.data?.data ?? [];

    // A FAILED connectivity read is not an empty one. Empty says "no record",
    // which the model reports honestly as unknown; a failure arriving as an
    // empty array would look identical and would be a different claim.
    const connectivityRows =
      connectivity.status === "success" ? connectivity.data?.data ?? [] : null;
    // Unclipped, and only for the "online now" claim -- see UnitInput.
    const connectivityLiveRows =
      connectivity.status === "success" ? liveRows(connectivity.data) : null;

    // No comparable baseline: the comparison week ended before this sensor was
    // installed here, so every "previously" figure would describe another
    // kitchen. `prior: null` makes the model omit the comparison rather than
    // report a fall to zero -- the same reason it omits it for a sensor
    // installed on Thursday.
    const comparable =
      scope.cutoffMs == null || scope.cutoffMs <= window.priorStart;

    const unit = summariseUnit(
      {
        scsName,
        name: displayName || scsName,
        alarms: alarmRows,
        sessions: sessionRows,
        connectivity: connectivityRows,
        connectivityLive: connectivityLiveRows,
        // The same rows serve both periods; the model filters each by window --
        // unless this sensor has not been here long enough to have a baseline.
        prior: comparable ? { alarms: alarmRows, sessions: sessionRows } : null,
      },
      window,
      DEFAULT_SECTIONS
    );

    return {
      report: {
        scsName,
        name: unit.name,
        periodLabel: window.label,
        sections: [...DEFAULT_SECTIONS],
        unit,
        issues: issuesForUnit(unit, DEFAULT_SECTIONS),
      },
      status: "ready",
      window,
      tenure: scope,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, scsName, displayName, window, scope, settled]);
}
